import { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { ResourcesHubIcon } from '@rsos/assets/icons';
import { UnreadNotificationIcon } from '@rsos/components/capstone/UnreadNotificationIcon/UnreadNotificationIcon';
import { Box, Button } from '@rsos/components/capstone/base';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import {
  useHasUnreadTransportRequests,
  useIsResourcePanelOpen,
  toggleResourcePanel,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import styles from './ResourceRequestBtn.module.css';

interface ResourceRequestBtnProps {
  onTrack?: (params: { panelAction: 'open' | 'close' }) => void;
}

/**
 * Navigation button for opening the Resource Request Panel in emergency_data.
 * Mount only from RightHandButtonNav when fe-app-name is emergency_data.
 * Other portal bundles lack the resourceRequests and api reducers this component needs,
 * which causes hydration errors.
 */
export const ResourceRequestBtn: FunctionComponent<ResourceRequestBtnProps> = ({
  onTrack,
}) => {
  const hasAccessToAirAmbulance = useHasAccessToAirAmbulance();
  const dispatch = useDispatch();
  const hasUnreadTransportRequests = useHasUnreadTransportRequests();
  const isResourcePanelOpen = useIsResourcePanelOpen();

  const handleClick = () => {
    onTrack?.({ panelAction: isResourcePanelOpen ? 'close' : 'open' });
    dispatch(toggleResourcePanel());
  };

  if (!hasAccessToAirAmbulance) return null;

  return (
    <Box horizontal onClick={handleClick}>
      <span className={styles.resourceRequestNavButtonContainer}>
        <Button
          btnType="tertiary"
          size="small"
          type="button"
          dataName="resource-request-nav-button"
          aria-label="Request Resources"
        >
          <ResourcesHubIcon width={16} height={16} color="secondaryText" />
          {hasUnreadTransportRequests && (
            <UnreadNotificationIcon
              aria-hidden="true"
              data-testid="resource-request-nav-icon"
            />
          )}
        </Button>
      </span>
    </Box>
  );
};

export default ResourceRequestBtn;
